/**
 * #776 / GMA-2023: who may use caregiver web — the single source of truth.
 *
 * #764 introduced these rules inline in LoginPage's success callback, which
 * made them a login-time check rather than a policy. Session hydration is a
 * second entry point into the same authorization decision, and it did not
 * consult them: a Contact session already in localStorage kept full access
 * across a reload. Both doors now call this module.
 *
 * The claim shape is cortex-be's `shapeJwtUserObject`
 * (Controllers/AuthController.js): `isCaregiver` and `isContact` are `true`
 * or absent, `isAdmin` / `isSuperAdmin` are booleans only for caregivers.
 * A claim that is neither unset nor a boolean cannot be reasoned about and
 * fails closed — the same rule `canShowHomeClientsMap` applies in
 * src/utils/clientProfileAccess.js.
 */

export const WEB_ACCESS_DENIED_CONTACT = "contact";
export const WEB_ACCESS_DENIED_NOT_ALLOWED = "not_allowed";

const ROLE_CLAIMS = ["isContact", "isCaregiver", "isAdmin", "isSuperAdmin"];

// Malformed claims are not "not set". A role claim that is not a boolean
// ("true", 1, {}) is ambiguous, and ambiguity fails closed.
const unsetOrBoolean = (claim) =>
  claim === undefined || claim === null || typeof claim === "boolean";

/**
 * @param {unknown} user decoded JWT `user` claim, or the login payload's user
 * @returns {{allowed: boolean, reason: string|null}}
 */
export function evaluateCaregiverWebAccess(user) {
  if (!user || typeof user !== "object" || Array.isArray(user)) {
    return { allowed: false, reason: WEB_ACCESS_DENIED_NOT_ALLOWED };
  }
  if (!ROLE_CLAIMS.every((claim) => unsetOrBoolean(user[claim]))) {
    return { allowed: false, reason: WEB_ACCESS_DENIED_NOT_ALLOWED };
  }

  // The contact claim is an explicit VETO, evaluated BEFORE the staff
  // allowlist: a token carrying isContact alongside a caregiver or admin
  // claim is ambiguous. Checking the allowlist first would admit that token
  // on the staff claim.
  if (user.isContact === true) {
    return { allowed: false, reason: WEB_ACCESS_DENIED_CONTACT };
  }

  const mayUseWeb =
    user.isCaregiver === true || user.isAdmin === true || user.isSuperAdmin === true;
  return mayUseWeb
    ? { allowed: true, reason: null }
    : { allowed: false, reason: WEB_ACCESS_DENIED_NOT_ALLOWED };
}

export const mayUseCaregiverWeb = (user) => evaluateCaregiverWebAccess(user).allowed;

/**
 * The message shown for a refusal. Chosen by reason rather than by the caller,
 * so the login form and any future door cannot describe the same refusal
 * differently. An unrecognised reason falls back to the generic message —
 * never `undefined` at the user.
 */
export function webAccessDenialMessage(reason) {
  return reason === WEB_ACCESS_DENIED_CONTACT
    ? "Contact accounts use the CompassCare mobile apps only."
    : "User not allowed";
}
